// o4TailorResume.js
//
// This component is an o4‑mini compatible version of TailorResume.  It
// preserves the original behaviour of tailoring a resume to a job
// description while clearly signalling that it works with the o4
// configuration.  The key differences are the use of the renamed
// parseJobData and anchor helper modules (o4ParseJobData and
// o4AnchorHelper).  All API prompts remain unchanged because the
// server selects the appropriate AI configuration (o4 vs legacy) via
// the prompt name and internal mapping.  By separating this into a
// dedicated file, the app can choose between legacy and o4 flows as
// needed.

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import '../css/tailorResume.css';
// Import the renamed parser and anchor helper for o4
import parseJobData from './parseJobData';
import updateTokenUsage from './updateTokenUsage';
import { inferAnchorsWithAI } from './anchorHelper';
import { ThreeDots } from 'react-loader-spinner';
import { AI_FUNCTIONS_BASE_URL as FUNCTIONS_BASE_URL } from '../config/api';
import {
  normalizeResponsibilitySuggestions,
  splitHighlightedText,
} from './responsibilitySuggestionUtils';

// The heuristic soft skill inference function has been removed.  All soft skills must
// now originate from the AI parser or direct user input.

const WORK_HISTORY_FIELD_LABELS = {
  jobTitle: 'Job Title',
  employer: 'Employer',
  responsibilities: 'Responsibilities',
  keyAccomplishments: 'Key Accomplishments',
  startDate: 'Start Date',
  endDate: 'End Date',
  hoursPerWeek: 'Hours Per Week',
  payRate: 'Pay Rate',
  employmentType: 'Employment Type',
  canContactSupervisor: 'Supervisor Contact Permission',
};

const computeMissingFieldsForEntry = (entry) => Object.keys(WORK_HISTORY_FIELD_LABELS)
  .filter((key) => (
    !entry
    || entry[key] === undefined
    || entry[key] === null
    || String(entry[key]).trim() === ''
  ));

const TailorResume = ({ formData, setFormData, setTokenUsage, setUsagePercentage, tokenThreshold }) => {
  // Generic UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [carouselIndex, setCarouselIndex] = useState(0);

  // --- Private‑sector job description parsing state ---
  // Text area where the user pastes the job posting
  const [jobDescription, setJobDescription] = useState('');
  // Parsed fields from the job description
  const [experienceRequirements, setExperienceRequirements] = useState('');
  const [requiredSkills, setRequiredSkills] = useState([]);
  const [softSkills, setSoftSkills] = useState([]);
  const [jobKeywords, setJobKeywords] = useState([]);
  const [selectedJobKeywords, setSelectedJobKeywords] = useState([]);

  // Enhancement state for responsibilities, accomplishments and projects
  const [enhancedResponsibilities, setEnhancedResponsibilities] = useState([]);
  const [enhancedAccomplishments, setEnhancedAccomplishments] = useState([]);
  const [enhancedProjects, setEnhancedProjects] = useState([]);
  const [rewritingSuggestion, setRewritingSuggestion] = useState(null);
  const [responsibilityCategory, setResponsibilityCategory] = useState('verified');
  const [keywordRewriteDraft, setKeywordRewriteDraft] = useState(null);
  const [updatedSuggestionIndex, setUpdatedSuggestionIndex] = useState(null);

  // Additional UI state for selecting which field to enhance and which entry to apply to
  const [selectedEntryType, setSelectedEntryType] = useState('workHistory');

  /*
   * Track fields missing from the user's existing work history.  When the
   * user has added one or more work history entries, the resume lexicon
   * requires certain properties (job title, employer, responsibilities, key
   * accomplishments, start and end dates, hours per week, pay rate,
   * employment type, and supervisor contact permission) to be defined.  If
   * any of these are blank for a given entry we record them here so the
   * analyze screen can warn the user and provide quick editing controls.
   */
  const [missingFields, setMissingFields] = useState([]);

  // Human‑readable labels for work history fields.  These are used when
  // listing missing fields and in the edit portal.  If you add more fields
  // to workHistory later, update this map accordingly.
  /**
   * Derive an array describing missing fields for each work history entry.  Each
   * element contains the index of the entry and the array of missing field keys.
   * This runs whenever the formData changes.  We only compute missing
   * information for workHistory; if no work history exists, the result is an
   * empty array.
   */
  useEffect(() => {
    const results = [];
    if (Array.isArray(formData.workHistory)) {
      formData.workHistory.forEach((entry, index) => {
        if (entry?.showOnResume === false) return;
        const missing = computeMissingFieldsForEntry(entry);
        if (missing.length > 0) {
          results.push({ index, fields: missing });
        }
      });
    }
    setMissingFields(results);
  }, [formData.workHistory]);

  /**
   * Update a single field on a work history entry.  This helper is used by
   * the inline edit portal on the analyze screen.  It clones the workHistory
   * array, updates the requested value and writes it back into formData.
   *
   * @param {number} workIndex Index of the work history entry to modify.
   * @param {string} field The key of the field to update.
   * @param {string} value The new value to write into the entry.
   */
  const handleMissingFieldChange = (workIndex, field, value) => {
    setFormData((prev) => {
      const updatedWorkHistory = Array.isArray(prev.workHistory) ? [...prev.workHistory] : [];
      // If the requested index does not exist, return previous state unchanged.
      if (!updatedWorkHistory[workIndex]) return prev;
      updatedWorkHistory[workIndex] = {
        ...updatedWorkHistory[workIndex],
        [field]: value,
      };
      return {
        ...prev,
        workHistory: updatedWorkHistory,
      };
    });
  };

  // Removed validation for job series codes; not needed in private‑sector flow

  // Removed handleManualSubmit; replaced by handleJobDescriptionSubmit below

  // Removed handleJobSeriesSelect: no job series selection in private sector

  const getOptionLabel = (options, value) => {
    const option = options.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  const handleWorkHistorySelect = useCallback((index) => {
    setFormData((previous) => {
      const selectedWorkHistory = previous.workHistory[index];
      return {
        ...previous,
        selectedEntryType: 'workHistory',
        selectedWorkHistoryIndex: index,
        selectedWorkHistory: selectedWorkHistory,
        selectedResponsibilities: selectedWorkHistory.responsibilities,
        selectedAccomplishments: selectedWorkHistory.keyAccomplishments,
      };
    });
  }, [setFormData]);

  const handleEducationSelect = useCallback((index) => {
    setFormData((previous) => {
      const selectedEducation = previous.educationHistory[index];
      return {
        ...previous,
        selectedEntryType: 'educationHistory',
        selectedEducationIndex: index,
        selectedEducation: selectedEducation,
        selectedProjects: selectedEducation.significantProjects,
        selectedCourses: selectedEducation.relevantCourses,
      };
    });
  }, [setFormData]);

  useEffect(() => {
    if (carouselIndex === 2) {   // only when you're on Step 2
      const selectedWorkIndex = Number(formData.selectedWorkHistoryIndex);
      const selectedWorkIsVisible =
        Number.isInteger(selectedWorkIndex) &&
        formData.workHistory?.[selectedWorkIndex]?.showOnResume !== false;

      if (
        selectedEntryType === 'workHistory' &&
        formData.workHistory.some((job) => job?.showOnResume !== false) &&
        (!formData.selectedWorkHistory || !selectedWorkIsVisible)
      ) {
        const firstVisibleIndex = formData.workHistory.findIndex(
          (job) => job?.showOnResume !== false,
        );
        handleWorkHistorySelect(firstVisibleIndex);
      }

      if (
        selectedEntryType === 'workHistory' &&
        !formData.workHistory.some((job) => job?.showOnResume !== false) &&
        formData.selectedWorkHistory
      ) {
        setFormData((previous) => ({
          ...previous,
          selectedWorkHistoryIndex: null,
          selectedWorkHistory: null,
          selectedResponsibilities: '',
          selectedAccomplishments: '',
        }));
      }

      if (
        selectedEntryType === 'educationHistory' &&
        formData.educationHistory.length > 0 &&
        !formData.selectedEducation
      ) {
        handleEducationSelect(0);
      }
    }
  }, [
    carouselIndex,
    selectedEntryType,
    formData.workHistory,
    formData.educationHistory,
    formData.selectedWorkHistoryIndex,
    formData.selectedWorkHistory,
    formData.selectedEducation,
    handleWorkHistorySelect,
    handleEducationSelect,
    setFormData,
  ]);

  /**
   * Handle submission of the pasted job description. This calls the
   * `manualAnnouncementParse` endpoint and maps the AI‑parsed response
   * into our private‑sector fields. It also writes the legacy fields
   * (specializedExperience, competencies, knowledge, keywords) on
   * formData to preserve compatibility with the enhancement API.
   */
  const handleJobDescriptionSubmit = async () => {
    setIsLoading(true);
    setError(null);
    const text = jobDescription.trim();
    if (!text) {
      setError('Input cannot be empty. Please paste the job description.');
      setIsLoading(false);
      return;
    }
    try {
      const data = {
        prompt: 'manualAnnouncementParse',
        manuallyProvidedAnnouncementText: text,
        userID: formData.user,
      };
      const response = await axios.post(
        `${FUNCTIONS_BASE_URL}/api/jobSeriesSearchHttp`,
        data,
        { headers: { 'Content-Type': 'application/json' } },
      );
      const aiParsedData = response.data;
      const manualParsedData = parseJobData(aiParsedData);
      // Map to new categories
      setExperienceRequirements(manualParsedData.specializedExperience || '');
      setSoftSkills(manualParsedData.competencies || []);
      setRequiredSkills(manualParsedData.knowledge || []);
      setJobKeywords(manualParsedData.keywords || []);
      setSelectedJobKeywords([]);
      // Persist to formData
      setFormData((prev) => ({
        ...prev,
        experienceRequirements: manualParsedData.specializedExperience || '',
        softSkills: manualParsedData.competencies || [],
        requiredSkills: manualParsedData.knowledge || [],
        preferredSkills: [],
        educationRequirements: '',
        jobSummary: '',
        jobKeywords: manualParsedData.keywords || [],
        selectedJobKeywords: [],
        // maintain legacy fields
        specializedExperience: manualParsedData.specializedExperience || '',
        competencies: manualParsedData.competencies || [],
        knowledge: manualParsedData.knowledge || [],
        keywords: manualParsedData.keywords || [],
        series: '',
        parsedJobAnnouncement: {
          raw: aiParsedData,
          aiFormatted: aiParsedData.aiFormatted || null,
        },
      }));
      await updateTokenUsage(formData.user, tokenThreshold, setTokenUsage, setUsagePercentage);
      setError(null);
      // Automatically go to the summary screen (step 1) after analyze is complete
      setCarouselIndex(1);

    } catch (err) {
      if (err?.response?.status === 403) {
        console.error('Token limit exceeded:', err);
        setError('Token limit exceeded. Please contact support for additional tokens.');
      } else {
        console.error('Error parsing job description:', err);
        setError('Failed to parse job description. Please try again later.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Toggle selection of extracted job keywords. These keywords feed into
   * the resume lexicon when requesting AI bullet points. Selected
   * keywords override the full list of extracted keywords.
   */
  const handleSelectJobKeyword = (keyword) => {
    const current = selectedJobKeywords || [];
    const isSelected = current.includes(keyword);
    const updated = isSelected ? current.filter((k) => k !== keyword) : [...current, keyword];
    setSelectedJobKeywords(updated);
    setFormData((prev) => ({ ...prev, selectedJobKeywords: updated }));
  };

  /**
   * Toggle selection of soft or required skills extracted from the job posting.
   * Selecting a skill adds it to the global skills array on formData and
   * selectedSkills array.  Deselecting removes it.  This enables users to
   * quickly incorporate parsed skills into their resume without relying on
   * heuristics.
   * @param {Object|string} skill - The skill object or string returned from the parser.
   */
  const handleSelectSkill = (skill) => {
    // Determine a normalized skill name.  Skills may be objects returned by the parser,
    // or plain strings.  Prefer the 'primary' or 'name' field if present.
    const name = (typeof skill === 'object' && skill !== null)
      ? (skill.primary || skill.name || '')
      : String(skill || '');
    if (!name) return;
    setFormData((prev) => {
      const currentSkills = Array.isArray(prev.skills) ? [...prev.skills] : [];
      const idx = currentSkills.findIndex((s) => s === name);
      let updatedSkills;
      if (idx >= 0) {
        // Remove existing entry
        updatedSkills = currentSkills.filter((s) => s !== name);
      } else {
        updatedSkills = [...currentSkills, name];
      }
      return {
        ...prev,
        skills: updatedSkills,
        selectedSkills: updatedSkills,
      };
    });
  };

  const employmentTypeOptions = [
    { value: 'fulltime', label: 'Full-time' },
    { value: 'parttime', label: 'Part-time' },
  ];

  const payRateOptions = [
    { value: 'biweekly', label: 'Biweekly' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'fee_basis', label: 'Fee Basis' },
    { value: 'per_year', label: 'Per Year' },
    { value: 'per_day', label: 'Per Day' },
    { value: 'per_hour', label: 'Per Hour' },
    { value: 'per_month', label: 'Per Month' },
    { value: 'piece_work', label: 'Piece Work' },
    { value: 'student_stipend', label: 'Student Stipend' },
    { value: 'school_year', label: 'School Year' },
    { value: 'without_compensation', label: 'Without Compensation' },
  ];

  const canContactSupervisorOptions = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
  ];

  const [selectedField, setSelectedField] = useState('responsibilities'); // default to responsibilities

  const handleFieldSelection = (field) => {
    setSelectedField(field);
    setError(null);
  };

  // Removed handleManualInputChange: job series codes are not used

  // Removed handleManualInputSubmit: keyword extraction is not needed in private sector

  // Removed handleUrlChange and handleParseAnnouncement: we parse the pasted description instead of a URL

  // Removed handleSelectKeyword: selection now handled by handleSelectJobKeyword

  // Removed filteredJobSeries: not needed for private sector

  const parseEnhancedResponse = (response) => {
    // Find the first occurrence of both 'AI:' and 'USER:'
    const aiIndex = response.indexOf('AI:');
    const userIndex = response.indexOf('User:');

    // Determine which occurs first, and use that as the delimiter
    let firstIndex = -1;

    if (aiIndex !== -1 && userIndex !== -1) {
      // Both 'AI:' and 'USER:' exist, so take the first one
      firstIndex = Math.min(aiIndex, userIndex);
    } else if (aiIndex !== -1) {
      // Only 'AI:' exists
      firstIndex = aiIndex;
    } else if (userIndex !== -1) {
      // Only 'USER:' exists
      firstIndex = userIndex;
    }

    // If a delimiter is found, use it as the initial cleaned response; otherwise, use the entire response.
    let cleanedResponse = firstIndex !== -1 ? response.substring(0, firstIndex).trim() : response.trim();

    // Now scan character‑by‑character to stop at the first '<' character if it exists
    let result = '';
    for (let i = 0; i < cleanedResponse.length; i++) {
      if (cleanedResponse[i] === '<') {
        break;
      }
      result += cleanedResponse[i];
    }

    // Trim any extra whitespace from the result and return
    return result.trim();
  };

  // Build a responsibility lexicon directly from existing form data. The
  // backend now grounds suggestions only in user-written responsibilities, so
  // a separate AI anchor request would add latency without improving evidence.
  const formResumeLexicon = (formData) => {
    const selectedWorkHistory = formData.workHistory[formData.selectedWorkHistoryIndex] || {};
    // Do not infer additional soft skills from education.  Use only existing competencies and skills.
    const existingCompetencies = formData.softSkills && formData.softSkills.length > 0
      ? formData.softSkills
      : (formData.competencies || []);
    const combinedCompetencies = Array.isArray(existingCompetencies) ? [...existingCompetencies] : [];
    // Candidate skills are evidence; target-job requirements are context only.
    // Sending required technologies as candidate skills caused unrelated roles
    // to be rewritten as if the user had performed that technical work.
    const existingSkills = Array.isArray(formData.skills) ? formData.skills : [];
    let combinedSkills = [...existingSkills];
    return {
      skills: combinedSkills,
      // Map modern fields back to legacy names for compatibility
      specializedExperience: formData.experienceRequirements || formData.specializedExperience,
      competencies: combinedCompetencies,
      knowledge: formData.requiredSkills || formData.knowledge,
      // Use selected job keywords if provided; otherwise default to all extracted keywords
      keywords: (formData.selectedJobKeywords && formData.selectedJobKeywords.length > 0)
        ? formData.selectedJobKeywords
        : (formData.jobKeywords || formData.keywords),
      series: formData.series,
      workHistory: {
        jobTitle: selectedWorkHistory.jobTitle,
        employer: selectedWorkHistory.employer,
        [selectedField]: selectedField === 'responsibilities'
          ? selectedWorkHistory.responsibilities
          : selectedWorkHistory.keyAccomplishments,
      },
      courses: formData.courses,
    };
  };

  // Build the resume lexicon for accomplishments enhancement.  Mirrors
  // formResumeLexicon but omits specializedExperience and ensures the same
  // AI anchor inference and soft skill augmentation.
  const formResumeLexiconForAccomplishments = async (formData) => {
    const selectedWorkHistory = formData.workHistory[formData.selectedWorkHistoryIndex] || {};
    let anchors;
    try {
      anchors = await inferAnchorsWithAI(selectedWorkHistory, formData.user);
    } catch (e) {
      console.error('Failed to infer anchors with AI:', e);
      // Fallback to a minimal anchors object containing only the employer when AI inference fails.
      anchors = { employer: selectedWorkHistory?.employer || '' };
    }
    // Do not infer additional soft skills from education.  Use only existing competencies and skills.
    const existingCompetencies = formData.softSkills && formData.softSkills.length > 0
      ? formData.softSkills
      : (formData.competencies || []);
    const combinedCompetencies = Array.isArray(existingCompetencies) ? [...existingCompetencies] : [];
    const existingSkills = Array.isArray(formData.skills) ? formData.skills : [];
    const combinedSkills = [...existingSkills];
    return {
      skills: combinedSkills,
      competencies: combinedCompetencies,
      knowledge: formData.requiredSkills || formData.knowledge,
      keywords:
        (formData.selectedJobKeywords && formData.selectedJobKeywords.length > 0)
          ? formData.selectedJobKeywords
          : (formData.jobKeywords || formData.keywords),
      series: formData.series,
      workHistory: {
        jobTitle: selectedWorkHistory.jobTitle,
        employer: selectedWorkHistory.employer,
        [selectedField]: selectedField === 'responsibilities'
          ? selectedWorkHistory.responsibilities
          : selectedWorkHistory.keyAccomplishments,
        anchors: anchors,
      },
      courses: formData.courses,
    };
  };

  // Build the resume lexicon for project enhancement.  This variant
  // incorporates inferred soft skills into the competencies and skills arrays.
  const formResumeLexiconForProjects = async (formData) => {
    const selectedEducation = formData.educationHistory[formData.selectedEducationIndex] || {};
    // Do not infer additional soft skills from education.  Use only existing competencies and skills.
    const existingCompetencies = formData.softSkills && formData.softSkills.length > 0
      ? formData.softSkills
      : (formData.competencies || []);
    const combinedCompetencies = Array.isArray(existingCompetencies) ? [...existingCompetencies] : [];
    const existingSkills = Array.isArray(formData.skills) ? formData.skills : [];
    const combinedSkills = [...existingSkills];
    return {
      skills: combinedSkills,
      specializedExperience: formData.experienceRequirements || formData.specializedExperience,
      competencies: combinedCompetencies,
      knowledge: formData.requiredSkills || formData.knowledge,
      keywords:
        (formData.selectedJobKeywords && formData.selectedJobKeywords.length > 0)
          ? formData.selectedJobKeywords
          : (formData.jobKeywords || formData.keywords),
      series: formData.series,
      education: {
        institution: selectedEducation.institution,
        degree: selectedEducation.degree,
        significantProjects: selectedEducation.significantProjects,
      },
      courses: selectedEducation.relevantCourses,
    };
  };

  const handleEnhanceWorkHistory = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Before making the API call, check if there are missing fields for the selected work entry.  If
      // any fields are missing, record them in the error state so the user sees a warning.  We
      // continue with the enhancement regardless of the warning.
      const selectedIndex = formData.selectedWorkHistoryIndex;
      const selectedEntry = Array.isArray(formData.workHistory) ? formData.workHistory[selectedIndex] : null;
      if (selectedEntry) {
        const missing = computeMissingFieldsForEntry(selectedEntry);
        if (missing.length > 0) {
          const names = missing.map((fieldKey) => WORK_HISTORY_FIELD_LABELS[fieldKey]).join(', ');
          setError(`Warning: the following fields are missing for this work entry: ${names}. The enhancement may be less accurate.`);
        }
      }
      // Build the resume lexicon asynchronously to include AI‑derived anchors and
      // inferred soft skills.  Await the promise before making the API call.
      const resumeLexicon = await formResumeLexicon(formData);
      console.log(resumeLexicon);
      const response = await axios.post(
        `${FUNCTIONS_BASE_URL}/api/jobSeriesSearchHttp`,
        {
          prompt: 'enhanceWorkHistory',
          resumeLexicon: resumeLexicon,
          updateField: selectedField,
          userID: formData.user,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      console.log(formData.user);
      console.log('Full Axios Response:', response.data); // Log full response for debugging

      const enhancedResults = response.data.enhancedResults;

      if (Array.isArray(enhancedResults) && enhancedResults.length > 0) {
        setEnhancedResponsibilities(
          normalizeResponsibilitySuggestions(enhancedResults, parseEnhancedResponse)
        );
        setUpdatedSuggestionIndex(null);
        updateTokenUsage(
          formData.user,
          tokenThreshold,
          setTokenUsage,
          setUsagePercentage
        );
      } else {
        console.error('enhancedResults is empty or not an array');
        setEnhancedResponsibilities([]); // Set an empty array if no results
      }

      setError(null);
    } catch (error) {
      if (error.response && error.response.status === 403) {
        console.error('Token limit exceeded:', error);
        setError('Token limit exceeded. Please contact support for additional tokens.');
      } else {
        console.error('Error fetching enhancements:', error);
        setError('Failed to enhance work history. Please try again later.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnhanceAccomplishments = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // As with responsibilities, warn if the selected work entry has missing fields.
      const selectedIndex = formData.selectedWorkHistoryIndex;
      const selectedEntry = Array.isArray(formData.workHistory) ? formData.workHistory[selectedIndex] : null;
      if (selectedEntry) {
        const missing = computeMissingFieldsForEntry(selectedEntry);
        if (missing.length > 0) {
          const names = missing.map((fieldKey) => WORK_HISTORY_FIELD_LABELS[fieldKey]).join(', ');
          setError(`Warning: the following fields are missing for this work entry: ${names}. The enhancement may be less accurate.`);
        }
      }
      // Build the resume lexicon asynchronously to include AI‑derived anchors and
      // inferred soft skills.  Await the promise before making the API call.
      const resumeLexicon = await formResumeLexiconForAccomplishments(formData); // Assuming this function is specific to accomplishments
      const response = await axios.post(
        `${FUNCTIONS_BASE_URL}/api/jobSeriesSearchHttp`,
        {
          prompt: 'enhanceWorkHistory',
          resumeLexicon: resumeLexicon,
          updateField: selectedField, // Ensure this explicitly reflects key accomplishments
          userID: formData.user,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      await updateTokenUsage(formData.user, tokenThreshold, setTokenUsage, setUsagePercentage);

      console.log('Full Axios Response:', response.data); // Log full response for debugging

      const enhancedResults = response.data.enhancedResults;

      if (Array.isArray(enhancedResults) && enhancedResults.length > 0) {
        const cleanedEnhancements = enhancedResults.map((item) =>
          parseEnhancedResponse(item.enhancement)
        );
        // Deduplicate case‑insensitively
        const seen = new Set();
        const uniqueEnhancements = [];
        cleanedEnhancements.forEach((text) => {
          const key = text.trim().toLowerCase();
          if (!seen.has(key)) {
            seen.add(key);
            uniqueEnhancements.push(text.trim());
          }
        });
        // Limit to first 5 unique items
        setEnhancedAccomplishments(uniqueEnhancements.slice(0, 10)); // Update accomplishments state
      } else {
        console.error('enhancedResults is empty or not an array');
        setEnhancedAccomplishments([]); // Clear state if no results
      }

      setError(null);
    } catch (error) {
      if (error.response && error.response.status === 403) {
        console.error('Token limit exceeded:', error);
        setError('Token limit exceeded. Please contact support for additional tokens.');
      } else {
        console.error('Error fetching enhancements:', error);
        setError('Failed to enhance work history. Please try again later.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnhanceProjects = async () => {
    setIsLoading(true);
    setError(null);

    try {
        // Build the resume lexicon asynchronously to include inferred soft skills.
        const resumeLexicon = await formResumeLexiconForProjects(formData); // Use your existing lexicon function
        const response = await axios.post(
            `${FUNCTIONS_BASE_URL}/api/jobSeriesSearchHttp`,
            {
                prompt: 'enhanceWorkHistory',
                resumeLexicon: resumeLexicon,
                updateField: 'significantProjects',
                userID: formData.user,
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        );

        await updateTokenUsage(formData.user, tokenThreshold, setTokenUsage, setUsagePercentage);

        console.log('Full Axios Response:', response.data); // Log full response for debugging

        const enhancedResults = response.data.enhancedResults;

        if (Array.isArray(enhancedResults) && enhancedResults.length > 0) {
            // Apply parseEnhancedResponse to clean each enhancement
            const cleanedEnhancements = enhancedResults.map((item) =>
                parseEnhancedResponse(item.enhancement)
            );
            // Deduplicate case‑insensitively
            const seen = new Set();
            const uniqueEnhancements = [];
            cleanedEnhancements.forEach((text) => {
                const key = text.trim().toLowerCase();
                if (!seen.has(key)) {
                    seen.add(key);
                    uniqueEnhancements.push(text.trim());
                }
            });
            setEnhancedProjects(uniqueEnhancements.slice(0, 5));
        } else {
            console.error('enhancedResults is empty or not an array');
            setEnhancedProjects([]); // Set an empty array if no results
        }

        setError(null);
      } catch (error) {
        if (error.response && error.response.status === 403) {
          console.error('Token limit exceeded:', error);
          setError('Token limit exceeded. Please contact support for additional tokens.');
        } else {
          console.error('Error fetching enhancements:', error);
          setError('Failed to enhance work history. Please try again later.');
        }
      } finally {
        setIsLoading(false);
      }
    };

  const toggleEnhancedProject = (enhancement) => {
    const fullEnhancement = enhancement.trim();

    setFormData((prevFormData) => {
      const selectedIndex = prevFormData.selectedEducationIndex;
      const updatedEducationHistory = [...prevFormData.educationHistory];

      // Get or initialize the selected education entry
      const educationEntry = updatedEducationHistory[selectedIndex] || {};
      
      // Initialize or update enhancedProjects within the specific education entry as an array
      const currentEnhancedProjects = Array.isArray(educationEntry.enhancedProjects)
        ? [...educationEntry.enhancedProjects]
        : []; // Default to an empty array if undefined

      // Toggle the enhancement in the enhancedProjects array
      const newEnhancedProjects = currentEnhancedProjects.includes(fullEnhancement)
        ? currentEnhancedProjects.filter(project => project !== fullEnhancement) // Remove if it exists
        : [...currentEnhancedProjects, fullEnhancement]; // Add if it doesn't exist

      // Update the selected education entry with the modified enhancedProjects
      updatedEducationHistory[selectedIndex] = {
        ...educationEntry,
        enhancedProjects: newEnhancedProjects, // Update enhancedProjects field
      };

      return {
        ...prevFormData,
        educationHistory: updatedEducationHistory,
      };
    });
  };

  const toggleEnhancedResponsibility = (enhancement) => {
    const fullEnhancement = enhancement.trim();

    setFormData(prevFormData => {
      const selectedIndex = Number(prevFormData.selectedWorkHistoryIndex);
      if (isNaN(selectedIndex) || !prevFormData.workHistory[selectedIndex]) {
        return prevFormData;
      }

      const updatedWorkHistory = [...prevFormData.workHistory];
      const workEntry = updatedWorkHistory[selectedIndex];

      const current = Array.isArray(workEntry.enhancedResponsibilities)
        ? [...workEntry.enhancedResponsibilities]
        : [];

      const newList = current.includes(fullEnhancement)
        ? current.filter(item => item !== fullEnhancement)
        : [...current, fullEnhancement];

      updatedWorkHistory[selectedIndex] = {
        ...workEntry,
        enhancedResponsibilities: newList,
      };

      return { ...prevFormData, workHistory: updatedWorkHistory };
    });
  };

  const rewriteResponsibilitySuggestion = async (
    suggestion,
    index,
    rewriteMode,
    selectedKeyword = ''
  ) => {
    setRewritingSuggestion({ index, mode: rewriteMode });
    setError(null);
    const rewriteUrl = `${FUNCTIONS_BASE_URL}/api/jobSeriesSearchHttp`;
    const rewriteStarted = performance.now();
    console.info('[Responsibility rewrite] Starting request', {
      url: rewriteUrl,
      mode: rewriteMode,
      suggestionIndex: index,
      inputCharacters: suggestion.text?.length || 0,
      userPresent: Boolean(formData.user),
    });
    try {
      const response = await axios.post(
        rewriteUrl,
        {
          prompt: 'enhanceWorkHistory',
          updateField: 'responsibilities',
          userID: formData.user,
          rewriteMode,
          suggestion: suggestion.text,
          selectedKeyword,
        },
        { headers: { 'Content-Type': 'application/json' } }
      );
      console.info('[Responsibility rewrite] Response received', {
        status: response.status,
        elapsedMilliseconds: Math.round(performance.now() - rewriteStarted),
        resultCount: response.data?.enhancedResults?.length || 0,
      });
      const rewrittenResult = normalizeResponsibilitySuggestions(
        response.data?.enhancedResults,
        parseEnhancedResponse
      )[0];
      if (!rewrittenResult) {
        throw new Error('The rewrite did not return a suggestion.');
      }
      const rewritten = {
        ...rewrittenResult,
        keywordMetadata: rewriteMode === 'keyword'
          ? {
              term: selectedKeyword,
              category: suggestion.keywordMetadata?.category || 'transferable',
              intent: `Uses the selected keyword: ${selectedKeyword}`,
            }
          : {
              ...suggestion.keywordMetadata,
              intent: rewriteMode === 'natural'
                ? 'Reworded to sound more natural while staying in this category.'
                : suggestion.keywordMetadata?.intent,
            },
      };

      setEnhancedResponsibilities((current) => current.map((item, itemIndex) => (
        itemIndex === index ? rewritten : item
      )));
      updateTokenUsage(
        formData.user,
        tokenThreshold,
        setTokenUsage,
        setUsagePercentage
      );
      setFormData((current) => {
        const selectedIndex = Number(current.selectedWorkHistoryIndex);
        const workHistory = [...(current.workHistory || [])];
        const workEntry = workHistory[selectedIndex];
        if (!workEntry || !Array.isArray(workEntry.enhancedResponsibilities)) return current;
        workHistory[selectedIndex] = {
          ...workEntry,
          enhancedResponsibilities: workEntry.enhancedResponsibilities.map((text) => (
            text === suggestion.text ? rewritten.text : text
          )),
        };
        return { ...current, workHistory };
      });
      if (rewriteMode === 'keyword') {
        setKeywordRewriteDraft(null);
      }
      setUpdatedSuggestionIndex(index);
    } catch (rewriteError) {
      const responseData = rewriteError.response?.data;
      console.error('[Responsibility rewrite] Request failed', {
        url: rewriteUrl,
        mode: rewriteMode,
        elapsedMilliseconds: Math.round(performance.now() - rewriteStarted),
        status: rewriteError.response?.status,
        response: responseData,
        message: rewriteError.message,
      });
      const requestId = responseData?.requestId;
      setError(
        requestId
          ? `Failed to rewrite this suggestion. Request ID: ${requestId}`
          : 'Failed to rewrite this suggestion. Please try again.'
      );
    } finally {
      setRewritingSuggestion(null);
    }
  };

  const renderResponsibilitySuggestions = () => {
    const selectedIndex = formData.selectedWorkHistoryIndex;
    const selectedWorkEntry = formData.workHistory[selectedIndex] || {};
    const selected = selectedWorkEntry.enhancedResponsibilities || [];
    const categories = [
      { id: 'verified', label: 'Verified keywords' },
      { id: 'aligned', label: 'Evidence aligned' },
      { id: 'transferable', label: 'Transferable skills' },
    ];
    const categorizedSuggestions = enhancedResponsibilities.map((suggestion, originalIndex) => {
      const category = ['verified', 'aligned'].includes(
        suggestion.keywordMetadata?.category
      )
        ? suggestion.keywordMetadata.category
        : 'transferable';
      return { suggestion, originalIndex, category };
    });
    const categoryCounts = categories.reduce((counts, category) => ({
      ...counts,
      [category.id]: categorizedSuggestions.filter(
        (item) => item.category === category.id
      ).length,
    }), {});
    const availableCategory = categoryCounts[responsibilityCategory]
      ? responsibilityCategory
      : categories.find((category) => categoryCounts[category.id] > 0)?.id;
    const visibleSuggestions = categorizedSuggestions.filter(
      (item) => item.category === availableCategory
    );
    const keywordLabel = (value) => {
      if (typeof value === 'string') return value.trim();
      return String(
        value?.primary || value?.name || value?.skill || value?.keyword || ''
      ).trim();
    };
    const keywordOptions = [
      ...(selectedJobKeywords || []),
      ...(jobKeywords || []),
      ...(requiredSkills || []),
      ...enhancedResponsibilities.map(
        (suggestion) => suggestion.keywordMetadata?.term
      ),
    ].reduce((options, value) => {
      const label = keywordLabel(value);
      if (
        label
        && !options.some((option) => option.toLowerCase() === label.toLowerCase())
      ) {
        options.push(label);
      }
      return options;
    }, []);

    return (
      <div className="responsibility-suggestions">
        <div
          className="keyword-category-tabs"
          role="tablist"
          aria-label="Suggestion relevance categories"
        >
          {categories.map((category) => {
            const isActive = category.id === availableCategory;
            const count = categoryCounts[category.id];
            return (
              <button
                key={category.id}
                type="button"
                role="tab"
                className={`keyword-category-tab ${category.id} ${isActive ? 'active' : ''}`}
                aria-selected={isActive}
                aria-controls="responsibility-suggestion-panel"
                disabled={!count}
                onClick={() => setResponsibilityCategory(category.id)}
              >
                <i className={`keyword-swatch ${category.id}`} aria-hidden="true" />
                <span>{category.label}</span>
                <strong>{count}</strong>
              </button>
            );
          })}
        </div>
        <p className="keyword-category-help">
          Select a category to compare its suggestions. You can choose resume bullets from more than one category.
        </p>
        <div
          id="responsibility-suggestion-panel"
          className="responsibility-suggestion-panel"
          role="tabpanel"
        >
        {visibleSuggestions.map(({ suggestion, originalIndex: index, category }) => {
          const metadata = suggestion.keywordMetadata || {};
          const isSelected = selected.includes(suggestion.text);
          const isRewriting = rewritingSuggestion?.index === index;
          const rewriteMode = isRewriting ? rewritingSuggestion.mode : null;
          const keywordDraft = keywordRewriteDraft?.index === index
            ? keywordRewriteDraft
            : null;
          const wasUpdated = updatedSuggestionIndex === index;
          return (
            <article
              key={`${suggestion.text}-${index}`}
              className={`responsibility-suggestion-card ${isSelected ? 'selected' : ''} ${wasUpdated ? 'updated' : ''}`}
            >
              <button
                type="button"
                className="responsibility-suggestion-select"
                onClick={() => toggleEnhancedResponsibility(suggestion.text)}
                aria-pressed={isSelected}
              >
                <span className={`keyword-badge ${category}`}>
                  {
                    category === 'verified'
                      ? 'Verified'
                      : category === 'aligned'
                        ? 'Evidence aligned'
                        : 'Transferable'
                  }
                </span>
                <span className="suggestion-text">
                  {splitHighlightedText(suggestion.text, metadata.term).map((part, partIndex) => (
                    part.highlighted
                      ? <mark className={`keyword-highlight ${category}`} key={partIndex}>{part.text}</mark>
                      : <React.Fragment key={partIndex}>{part.text}</React.Fragment>
                  ))}
                </span>
                <span className="selection-hint">
                  {wasUpdated
                    ? 'Updated'
                    : isSelected
                      ? 'Selected for resume'
                      : 'Select for resume'}
                </span>
              </button>
              <div className="suggestion-relevance">
                <strong>{metadata.term ? `Keyword: ${metadata.term}` : 'Grounded in this work entry'}</strong>
              </div>
              <div className="suggestion-rewrite-actions">
                <button
                  type="button"
                  disabled={isRewriting}
                  onClick={() => rewriteResponsibilitySuggestion(suggestion, index, 'accuracy')}
                >
                  {rewriteMode === 'accuracy' ? 'Checking accuracy...' : 'Make More Accurate'}
                </button>
                <button
                  type="button"
                  disabled={isRewriting}
                  onClick={() => rewriteResponsibilitySuggestion(suggestion, index, 'natural')}
                >
                  {rewriteMode === 'natural' ? 'Improving wording...' : 'Make More Natural'}
                </button>
                <button
                  type="button"
                  disabled={isRewriting}
                  onClick={() => setKeywordRewriteDraft({
                    index,
                    keyword: metadata.term || '',
                  })}
                >
                  Add a Keyword
                </button>
              </div>
              {keywordDraft && (
                <div className="keyword-rewrite-panel">
                  <div className="keyword-rewrite-heading">
                    <strong>Add a keyword to this suggestion</strong>
                    <span>Choose a job keyword or type your own.</span>
                  </div>
                  {keywordOptions.length > 0 && (
                    <div className="keyword-rewrite-options" aria-label="Available keywords">
                      {keywordOptions.map((keyword) => (
                        <button
                          key={keyword}
                          type="button"
                          className={
                            keywordDraft.keyword.toLowerCase() === keyword.toLowerCase()
                              ? 'selected'
                              : ''
                          }
                          onClick={() => setKeywordRewriteDraft({
                            ...keywordDraft,
                            keyword,
                          })}
                        >
                          {keyword}
                        </button>
                      ))}
                    </div>
                  )}
                  <label className="keyword-rewrite-custom">
                    Or type a keyword
                    <input
                      type="text"
                      value={keywordDraft.keyword}
                      maxLength={80}
                      placeholder="Exact keyword or phrase"
                      onChange={(event) => setKeywordRewriteDraft({
                        ...keywordDraft,
                        keyword: event.target.value,
                      })}
                    />
                  </label>
                  <div className="keyword-rewrite-controls">
                    <button
                      type="button"
                      onClick={() => setKeywordRewriteDraft(null)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={isRewriting || !keywordDraft.keyword.trim()}
                      onClick={() => rewriteResponsibilitySuggestion(
                        suggestion,
                        index,
                        'keyword',
                        keywordDraft.keyword.trim()
                      )}
                    >
                      {rewriteMode === 'keyword'
                        ? 'Rewriting...'
                        : 'Rewrite with Keyword'}
                    </button>
                  </div>
                </div>
              )}
              {isRewriting && (
                <div className="suggestion-rewrite-status" role="status" aria-live="polite">
                  <ThreeDots height="12" width="30" color="#4f6ef7" ariaLabel="rewriting" visible />
                  Rewriting this suggestion
                </div>
              )}
            </article>
          );
        })}
        </div>
      </div>
    );
  };

  const toggleEnhancedAccomplishment = (enhancement) => {
    const fullEnhancement = enhancement.trim();

    setFormData((prevFormData) => {
      const selectedIndex = prevFormData.selectedWorkHistoryIndex;
      const updatedWorkHistory = [...prevFormData.workHistory];

      // Initialize or update enhancedAccomplishments within the specific work entry
      const workEntry = updatedWorkHistory[selectedIndex] || {};
      const updatedEnhancedAccomplishments = Array.isArray(workEntry.enhancedAccomplishments)
        ? [...workEntry.enhancedAccomplishments]
        : []; // Initialize as an empty array if undefined

      // Add or remove the enhancement
      const newEnhancedAccomplishments = updatedEnhancedAccomplishments.includes(fullEnhancement)
        ? updatedEnhancedAccomplishments.filter(item => item !== fullEnhancement) // Remove if exists
        : [...updatedEnhancedAccomplishments, fullEnhancement]; // Add if doesn't exist

      // Update the selected work entry with the modified enhancedAccomplishments
      updatedWorkHistory[selectedIndex] = {
        ...workEntry,
        enhancedAccomplishments: newEnhancedAccomplishments,
      };

      return {
        ...prevFormData,
        workHistory: updatedWorkHistory,
      };
    });
  };

  const renderEnhancedButtons = (enhancements, toggleFunction) => {
    const selectedIndex = formData.selectedWorkHistoryIndex;
    const selectedWorkEntry = formData.workHistory[selectedIndex] || {};
    const currentEnhancements = enhancements === enhancedResponsibilities 
      ? selectedWorkEntry.enhancedResponsibilities || [] 
      : selectedWorkEntry.enhancedAccomplishments || [];

    return (
      <div className="enhanced-buttons">
        {enhancements.map((enhancement, index) => {
          const fullEnhancement = enhancement.trim();
          const isSelected = currentEnhancements.includes(fullEnhancement);

          return (
            <button
              key={index}
              className={`enhanced-item-button ${isSelected ? 'selected' : ''}`}
              onClick={() => toggleFunction(fullEnhancement)}
              title={enhancement}
            >
              {fullEnhancement.split(':')[0]}
            </button>
          );
        })}
        

      </div>
    );
  };

  // Render carousel content for Auto Parse mode
// Render carousel content for Auto Parse mode
const renderAutoParseCarousel = () => {
  switch (carouselIndex) {
    case 0: // Step 1: paste JD and analyze
      return (
        <>
          <h1>Tailor Your Resume</h1>
          <div className="manual-parse-carousel">
            <div className="manual-input-section">
              <textarea
                placeholder="Paste the job description here..."
                className="manual-input"
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
              />
            </div>

            <button
              type="button"
              onClick={handleJobDescriptionSubmit}
              className="manual-parse-submit-button"
              disabled={isLoading}
              aria-busy={isLoading}
              aria-live="polite"
              style={{ marginTop: '20px', borderTop: '1px solid #ddd', paddingTop: '10px' }}
            >
              {isLoading ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <ThreeDots height="14" width="36" color="#ffffff" ariaLabel="loading" visible />
                  Analyzing…
                </span>
              ) : (
                'Analyze Description'
              )}
            </button>
            

            {error && <p className="error-message">{error}</p>}
          </div>

          {/* Navigation for Step 1 */}
          <div className="carousel-navigation">
            <button
              className="carousel-navigation"
              onClick={() => setCarouselIndex(1)}
            >
              Next
            </button>
          </div>
        </>
      );

    case 1: // Step 2: summary + keyword/skill selection
      return (
        <>
          <h1>Job Description Summary</h1>

          <div className="parsed-data-section">
            <div className="responsive-box">
              <h1>Experience Requirements</h1>
              <div className="scrollable-content">
                {experienceRequirements
                  ? experienceRequirements
                  : 'No experience requirements were discovered in this job description.'}
              </div>
            </div>

            <div className="responsive-box">
              <h1>Required Skills</h1>
              <div className="scrollable-content">
                {Array.isArray(requiredSkills) && requiredSkills.length > 0 ? (
                  <ul className="keywords-list">
                    {requiredSkills.map((skill, index) => {
                      const isObj = typeof skill === 'object' && skill !== null;
                      const name = isObj ? (skill.name || skill.primary || '') : String(skill || '');
                      if (!name) return null;

                      const description = isObj && skill.description ? String(skill.description) : '';
                      const selectedSkills = Array.isArray(formData.skills) ? formData.skills : [];
                      const isSelected = selectedSkills.includes(name);

                      return (
                        <li key={index}>
                          <button
                            type="button"
                            className={`keywordItem ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleSelectSkill(skill)}
                            title={name}
                          >
                            {name}
                          </button>
                          {description ? (
                            <span className="skill-description">: {description}</span>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  'No required skills were discovered in this job description.'
                )}
              </div>
            </div>

            <div className="responsive-box">
              <h1>Soft Skills</h1>
              <div className="scrollable-content">
                {Array.isArray(softSkills) && softSkills.length > 0 ? (
                  <ul className="keywords-list">
                    {softSkills.map((skill, index) => {
                      const isObj = typeof skill === 'object' && skill !== null;
                      const name = isObj ? (skill.primary || skill.name || '') : String(skill || '');
                      if (!name) return null;

                      const subcat = isObj && skill.subcategory ? String(skill.subcategory) : '';
                      const description = isObj && skill.description ? String(skill.description) : '';
                      const selectedSkills = Array.isArray(formData.skills) ? formData.skills : [];
                      const isSelected = selectedSkills.includes(name);

                      return (
                        <li key={index}>
                          <button
                            type="button"
                            className={`keywordItem ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleSelectSkill(skill)}
                            title={name}
                          >
                            {name}
                          </button>
                          {subcat ? <em> {subcat}: </em> : null}
                          {description || ''}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  'No soft skills were discovered in this job description.'
                )}
              </div>
            </div>

            <div className="responsive-box">
              <h1>Keywords</h1>
              <div className="scrollable-content">
                {Array.isArray(jobKeywords) && jobKeywords.length > 0 ? (
                  <ul className="keywords-list">
                    {jobKeywords.map((keyword, index) => {
                      const isSelected = selectedJobKeywords.includes(keyword);
                      return (
                        <li key={index}>
                          <button
                            type="button"
                            className={`keywordItem ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleSelectJobKeyword(keyword)}
                            title={keyword}
                          >
                            {keyword}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  'No keywords were discovered in this job description.'
                )}
              </div>
            </div>
          </div>

          {/* Warning and inline edit portal for missing work history fields */}
          {missingFields.length > 0 && (
            <div className="missing-fields-portal">
              <h2>Missing Work History Details</h2>
              <p style={{ marginBottom: '0.5rem' }}>
                Some critical fields in your work history are blank. Completing them will allow the AI to
                better tailor bullet points to your experience. You can still proceed without filling
                these in, but the results may be less relevant.
              </p>
              {missingFields.map(({ index, fields }) => (
                <div key={index} className="missing-entry-editor">
                  <h3>Entry {index + 1}</h3>
                  {fields.map((fieldKey) => {
                    // Determine current value for the field
                    const currentValue = formData.workHistory && formData.workHistory[index]
                      ? formData.workHistory[index][fieldKey] || ''
                      : '';
                    // Render appropriate input element based on field type.  For certain
                    // enumerated fields we use a select; otherwise fall back to text input.
                    if (fieldKey === 'payRate') {
                      return (
                        <div key={fieldKey} style={{ marginBottom: '0.5rem' }}>
                          <label>{WORK_HISTORY_FIELD_LABELS[fieldKey]}
                            <select
                              value={currentValue}
                              onChange={(e) => handleMissingFieldChange(index, fieldKey, e.target.value)}
                            >
                              <option value="">-- Select --</option>
                              {payRateOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </label>
                        </div>
                      );
                    }
                    if (fieldKey === 'employmentType') {
                      return (
                        <div key={fieldKey} style={{ marginBottom: '0.5rem' }}>
                          <label>{WORK_HISTORY_FIELD_LABELS[fieldKey]}
                            <select
                              value={currentValue}
                              onChange={(e) => handleMissingFieldChange(index, fieldKey, e.target.value)}
                            >
                              <option value="">-- Select --</option>
                              {employmentTypeOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </label>
                        </div>
                      );
                    }
                    if (fieldKey === 'canContactSupervisor') {
                      return (
                        <div key={fieldKey} style={{ marginBottom: '0.5rem' }}>
                          <label>{WORK_HISTORY_FIELD_LABELS[fieldKey]}
                            <select
                              value={currentValue}
                              onChange={(e) => handleMissingFieldChange(index, fieldKey, e.target.value)}
                            >
                              <option value="">-- Select --</option>
                              {canContactSupervisorOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </label>
                        </div>
                      );
                    }
                    if (fieldKey === 'hoursPerWeek') {
                      return (
                        <div key={fieldKey} style={{ marginBottom: '0.5rem' }}>
                          <label>{WORK_HISTORY_FIELD_LABELS[fieldKey]}
                            <input
                              type="number"
                              value={currentValue}
                              onChange={(e) => handleMissingFieldChange(index, fieldKey, e.target.value)}
                              className="themed-input"
                            />
                          </label>
                        </div>
                      );
                    }
                    if (fieldKey === 'startDate' || fieldKey === 'endDate') {
                      return (
                        <div key={fieldKey} style={{ marginBottom: '0.5rem' }}>
                          <label>{WORK_HISTORY_FIELD_LABELS[fieldKey]}
                            <input
                              type="date"
                              value={currentValue}
                              onChange={(e) => handleMissingFieldChange(index, fieldKey, e.target.value)}
                              className="themed-input"
                            />
                          </label>
                        </div>
                      );
                    }
                    // Default text input for all other fields
                    return (
                      <div key={fieldKey} style={{ marginBottom: '0.5rem' }}>
                        <label>{WORK_HISTORY_FIELD_LABELS[fieldKey]}
                          <input
                            type="text"
                            value={currentValue}
                            onChange={(e) => handleMissingFieldChange(index, fieldKey, e.target.value)}
                            className="themed-input"
                          />
                        </label>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {error && <p className="error-message">{error}</p>}

          {/* Navigation for Step 2 */}
          <div className="carousel-nav-buttons">
            <button
              className="carousel-nav-button"
              onClick={() => setCarouselIndex(0)}
            >
              Back
            </button>
            <button
              className="carousel-nav-button"
              onClick={() => setCarouselIndex(2)}
            >
              Next
            </button>
          </div>
        </>
      );

    case 2: // Step 3: select entry to update
      return (
        <>
          <h1>Select Entry to Update</h1>

          {/* Toggle between Work History and Education */}
          <div className="selected-work-history-details">
            <label>
              <input
                type="radio"
                value="workHistory"
                checked={selectedEntryType === 'workHistory'}
                onChange={() => setSelectedEntryType('workHistory')}
              />
              {' '}Work History
            </label>

            <label>
              <input
                type="radio"
                value="educationHistory"
                checked={selectedEntryType === 'educationHistory'}
                onChange={() => setSelectedEntryType('educationHistory')}
              />
              {' '}Education
            </label>
          </div>

          {/* Conditionally render either work history or education list */}
          {selectedEntryType === 'workHistory' ? (
            <ul>
              {(formData.workHistory || []).map((job, index) => (
                job?.showOnResume !== false && (
                <li key={index}>
                  <button onClick={() => handleWorkHistorySelect(index)}>
                    {(job?.jobTitle || 'No Title')} at {(job?.employer || 'No Employer')}
                  </button>
                </li>
                )
              ))}
            </ul>
          ) : (
            <ul>
              {(formData.educationHistory || []).map((edu, index) => (
                <li key={index}>
                  <button onClick={() => handleEducationSelect(index)}>
                    {(edu?.institution || 'No Institution')} - {(edu?.degree || 'No Degree')}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Display selected details */}
          {selectedEntryType === 'workHistory' && formData.selectedWorkHistory && (
            <div className="selected-work-history-details">
              <h2>
                {formData.selectedWorkHistory.jobTitle} at {formData.selectedWorkHistory.employer}
              </h2>
              <p><strong>Responsibilities:</strong> {formData.selectedResponsibilities || 'No responsibilities listed'}</p>
              <p><strong>Key Accomplishments:</strong> {formData.selectedAccomplishments || 'No accomplishments listed'}</p>
              <p><strong>Start Date:</strong> {formData.selectedWorkHistory.startDate || 'N/A'}</p>
              <p><strong>End Date:</strong> {formData.selectedWorkHistory.endDate || 'N/A'}</p>
              <p><strong>Hours Per Week:</strong> {formData.selectedWorkHistory.hoursPerWeek || 'N/A'}</p>
              <p><strong>Pay Rate:</strong> {getOptionLabel(payRateOptions, formData.selectedWorkHistory.payRate)}</p>
              <p><strong>Employment Type:</strong> {getOptionLabel(employmentTypeOptions, formData.selectedWorkHistory.employmentType)}</p>
              <p><strong>Can Contact Supervisor:</strong> {getOptionLabel(canContactSupervisorOptions, formData.selectedWorkHistory.canContactSupervisor)}</p>
            </div>
          )}

          {selectedEntryType === 'educationHistory' && formData.selectedEducation && (
            <div className="selected-work-history-details">
              <h2>{formData.selectedEducation.institution} - {formData.selectedEducation.degree}</h2>
              <p><strong>Major:</strong> {formData.selectedEducation.major || 'No major listed'}</p>
              <p><strong>Graduation Year:</strong> {formData.selectedEducation.graduationYear || 'N/A'}</p>
              <p><strong>Relevant Courses:</strong> {formData.selectedCourses ? formData.selectedCourses.join(' | ') : 'No courses listed'}</p>
              <p><strong>Significant Projects:</strong> {formData.selectedProjects || 'No projects listed'}</p>
            </div>
          )}

          {/* Navigation for Step 3 */}
          <div className="carousel-nav-buttons">
            <button
              className="carousel-nav-button"
              onClick={() => setCarouselIndex(1)}
            >
              Back
            </button>
            <button
              className="carousel-nav-button"
              onClick={() => setCarouselIndex(3)}
            >
              Next
            </button>
          </div>
        </>
      );

    case 3: // Step 4: enhancements
      return (
        <div className="tailor-resume-screen">
          {selectedEntryType === 'workHistory' ? (
            <>
              <h1>Enhance Work History</h1>

              <div className="field-selection">
                <label>
                  <input
                    type="radio"
                    value="responsibilities"
                    checked={selectedField === 'responsibilities'}
                    onChange={() => handleFieldSelection('responsibilities')}
                  />
                  {' '}Responsibilities
                </label>
                <label>
                  <input
                    type="radio"
                    value="keyAccomplishments"
                    checked={selectedField === 'keyAccomplishments'}
                    onChange={() => handleFieldSelection('keyAccomplishments')}
                  />
                  {' '}Key Accomplishments
                </label>
              </div>


              <button
                type="button"
                onClick={
                  selectedField === 'keyAccomplishments'
                    ? handleEnhanceAccomplishments
                    : handleEnhanceWorkHistory
                }
                className="enhance-button"
                disabled={isLoading}
                aria-busy={isLoading}
                aria-live="polite"
                style={{ marginTop: '20px', borderTop: '1px solid #ddd', paddingTop: '10px' }}
              >
                {isLoading ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <ThreeDots height="14" width="36" color="#ffffff" ariaLabel="loading" visible />
                    Creating suggestions...
                  </span>
                ) : (
                  'Enhance'
                )}
              </button>


              {error && <p className="error-message">{error}</p>}

              {selectedField === 'responsibilities' &&
                Array.isArray(enhancedResponsibilities) &&
                enhancedResponsibilities.length > 0 && (
                <>
                  <h2>Suggested Responsibilities</h2>
                  {renderResponsibilitySuggestions()}
                </>
              )}

              {selectedField === 'keyAccomplishments' &&
                Array.isArray(enhancedAccomplishments) &&
                enhancedAccomplishments.length > 0 && (
                <>
                  <h2>Suggested Accomplishments</h2>
                  {renderEnhancedButtons(enhancedAccomplishments, toggleEnhancedAccomplishment)}
                </>
              )}

              {selectedField === 'responsibilities' &&
                (!enhancedResponsibilities || enhancedResponsibilities.length === 0) && (
                  <p>No responsibility suggestions available.</p>
                )}

              {selectedField === 'keyAccomplishments' &&
                (!enhancedAccomplishments || enhancedAccomplishments.length === 0) && (
                  <p>No accomplishment suggestions available.</p>
                )}
            </>
          ) : (
            <>
              <h1>Enhance Education Projects</h1>



              <button
                type="button"
                onClick={handleEnhanceProjects}
                className="enhance-button"
                disabled={isLoading}
                aria-busy={isLoading}
                aria-live="polite"
                style={{ marginTop: '20px', borderTop: '1px solid #ddd', paddingTop: '10px' }}
              >
                {isLoading ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <ThreeDots height="14" width="36" color="#ffffff" ariaLabel="loading" visible />
                    Enhancing…
                  </span>
                ) : (
                  'Enhance Projects'
                )}
              </button>


              {error && <p className="error-message">{error}</p>}

              {Array.isArray(enhancedProjects) && enhancedProjects.length > 0 ? (
                <>
                  <h2>Suggested Projects</h2>
                  <div className="enhanced-items">
                    {enhancedProjects.map((enhancement, index) => {
                      const selected = Boolean(
                        formData.educationHistory?.[formData.selectedEducationIndex]
                          ?.enhancedProjects?.includes(enhancement.trim())
                      );
                      return (
                        <button
                          key={index}
                          className={`enhanced-item-button ${selected ? 'selected' : ''}`}
                          onClick={() => toggleEnhancedProject(enhancement)}
                          title={enhancement}
                        >
                          {enhancement.split(':')[0]}
                        </button>
                      );
                    })}
                  </div>
                </>
              ) : (
                <p>No suggestions available for projects.</p>
              )}
            </>
          )}

          {/* Navigation for Step 4 */}
          <div className="carousel-nav-buttons">
            <button
              className="carousel-nav-button"
              onClick={() => setCarouselIndex(2)}
            >
              Back
            </button>
          </div>
        </div>
      );

    default:
      return null;
  }
};


  // Main render of the component
  return (
    <div className="tailor-resume-container">
      {renderAutoParseCarousel()}
    </div>
  );
};

export default TailorResume;
